import Axios from "../config/axios-v1-config";
import AxiosV2 from "../config/axios-v2-config";
import { errorHandler } from "../utils/errorHandler";
import {
  apiErrorMessage,
  apiBodyMessages,
  apiErrorData,
  apiErrorStatus,
} from "../utils/apiErrorMessage";

export const getUser = async ({ id }) => {
  try {
    const userData = await Axios.get(`users/${id}`);
    return userData;
  } catch (err) {
    // Manejar errores de CORS silenciosamente
    if (err.isCorsError || (!err.response && err.message)) {
      errorHandler.handleApiError(err, {
        showNotification: false, // No mostrar notificación para errores CORS
        logError: false, // No loguear en consola
        context: "Obtener usuario"
      });
      return { error: { message: 'Error de conexión con el servidor' } };
    }
    return { error: err.response?.data || { message: 'Error al obtener el usuario' } };
  }
};

export const getDeliveries = async (values) => {
  const deliveries = await Axios.post("users", values).catch(
    (err) => {
      return { error: err.response.data };
    }
  );

  return deliveries;
};

export const getDeliveriesListed = async (values) => {
  const deliveries = await Axios.post("users", values).catch((err) => {
    return { error: err.response.data };
  });

  return deliveries;
};

export const getDeliveryUsers = async (status, role, logisticCompanyId) => {
  try {
    const response = await Axios.get("users/status/user", {
      params: {
        status,
        role,
        logisticCompanyId,
        // 10000 (antes 1000): la cola de repartidores pendientes ya rozaba el
        // tope (987/1000) y al superarlo los registros nuevos desaparecían de
        // la vista SIN error. Parche provisional hasta paginar server-side.
        limit: 10000
      },
    });
    return response.data;
  } catch (err) {
    return { error: err.response?.data || 'Error al obtener deliverys' };
  }
};

// Resuelve correos → repartidores del operador (para la carga masiva de extras). El
// backend scopea por el operador del JWT (nunca del body). Devuelve [{email, driverId, name}].
export const resolveDeliveryEmails = (emails) =>
  Axios.post("users/resolve-delivery-emails", { emails });

// Usuarios de los COMERCIOS (clientes) del operador: todos los usuarios, de
// cualquier rol, cuya empresa tiene un contrato ACTIVO con el operador. El
// operador se deriva del JWT en el backend (aislamiento multi-tenant), así que
// no se envía. Devuelve un resultado paginado ({ docs, totalDocs, ... }).
export const getClientUsers = async ({ role, companyId, limit = 10000 } = {}) => {
  try {
    const response = await Axios.get("users/clients", {
      params: {
        ...(role ? { role } : {}),
        ...(companyId ? { companyId } : {}),
        limit,
      },
    });
    return response.data;
  } catch (err) {
    return { error: err.response?.data || "Error al obtener clientes" };
  }
};

export const getAllUsers = async () => {
  const users = await Axios.get("users").catch((err) => {
    return { error: err.response.data };
  });

  return users;
};

// Obtiene un usuario por id (liviano). Usado p. ej. para mostrar el teléfono
// del repartidor asignado: `assignTo` viene poblado sin teléfono.
export const getUserById = async (id) => {
  try {
    const { data } = await Axios.get(`users/${id}`);
    return data?.data || data; // soporta { data: user } o el user directo
  } catch (err) {
    return { error: err.response?.data || "Error al obtener el usuario" };
  }
};

export const flexToken = async (values) => {
  const response = await Axios.post("https://app-qa.beartrackapp.com/mercadolibre/flex-token", values).catch((err) => {
    return { error: err.response.data };
  });

  return response;
};

export const deleteIntegrationApi = async (values) => {
  const response = await Axios.put("integration", values).catch((err) => {
    // err.response no existe en fallas de red: sin optional chaining el catch mismo lanzaba TypeError
    return { error: err?.response?.data };
  });

  return response;
};

//ksandoval 09-07-2024

export const getNewUsers = async (page = 1, limit = 10) => {
  return await Axios.get(`new-users?page=${page}&limit=${limit}`).catch(
    (err) => {
      return { error: err.response.data };
    }
  );
};

//este endpoint se usa para bloquear o desbloquear usuarios  - Dvlprchris
export const activateUserApi = async ({ userId, status }) => {
  const activatedUser = await Axios.patch(`users/${userId}`, {
    active: status,
  }).catch((err) => {
    return { error: err.response?.data || err.message };
  });

  return activatedUser;
};

export const getInActiveUsers = async (page = 1, limit = 10, searchTerm = "") => {
  let url = `inactiveUsers?page=${page}&limit=${limit}`;

  if (searchTerm !== "" && searchTerm !== null && searchTerm !== "null") {
    url = `inactiveUsers?page=${page}&limit=${limit}&searchTerm=${searchTerm}`;
  }

  return await Axios.get(url).catch((err) => {
    return { error: err.response.data };
  });
};

export const getInactiveDeliverers = async () => {
  try {
    // limit 10000 (antes 1000) en ambas sub-queries: la cola "por aceptar" iba
    // en 987/1000 — al pasar el tope, los autoregistros nuevos quedaban
    // truncados en silencio. Provisional hasta paginar server-side.
    // Obtener usuarios inactivos (active = false)
    const fetchInactive = async () =>
      Axios.get("users/status/user", {
        params: {
          status: "inactive",
          role: "delivery",
          limit: 10000,
        },
      }).then((r) => r.data).catch(() => ({ docs: [] }));

    // Obtener usuarios activos sin logisticCompanyId (active = true y sin flota)
    const fetchActiveWithoutFleet = async () =>
      Axios.get("users/status/user", {
        params: {
          status: "active",
          role: "delivery",
          withoutLogisticCompany: true,
          limit: 10000,
        },
      }).then((r) => r.data).catch(() => ({ docs: [] }));

    const [inactive, activeWithoutFleet] = await Promise.all([
      fetchInactive(),
      fetchActiveWithoutFleet(),
    ]);

    // Unificar resultados sin duplicados
    const map = new Map();
    [...(inactive?.docs || inactive || []), ...(activeWithoutFleet?.docs || activeWithoutFleet || [])].forEach((u) => {
      if (!u) return;
      map.set(u._id || u.id, u);
    });

    return { docs: Array.from(map.values()) };
  } catch (err) {
    return { error: err.response?.data || 'Error al obtener repartidores inactivos' };
  }
};


/**
 * Conteo de repartidores activos de una empresa logística
 * (users.active === true & role delivery & logisticCompanyId). Usa el endpoint
 * de conteo nuevo (microservice-users) y, si aún no existe, cae al listado
 * activo y cuenta en cliente. Devuelve { active, total }.
 */
export const getActiveDriversCount = async ({ logisticCompanyId } = {}) => {
  try {
    const response = await Axios.get("users/active-drivers/count", {
      params: { ...(logisticCompanyId ? { logisticCompanyId } : {}) },
    });
    const d = response.data || {};
    return { success: true, data: { active: d.active ?? 0, total: d.total ?? d.active ?? 0 }, error: null };
  } catch (err) {
    // Fallback: el endpoint de conteo aún no está desplegado → contar el listado.
    try {
      const list = await getActiveDeliveryUsers({ logisticCompanyId });
      const raw = list?.data;
      const arr = Array.isArray(raw) ? raw : raw?.docs || raw?.users || raw?.data || [];
      return { success: true, data: { active: arr.length, total: arr.length }, error: null };
    } catch (e) {
      return { success: false, data: null, error: err.response?.data || { message: "No se pudo obtener el conteo de repartidores activos" } };
    }
  }
};

export const getActiveDeliveryUsers = async ({ logisticCompanyId, limit = 10000 } = {}) => {
  try {
    const params = {
      status: "active",
      role: "delivery",
      limit,
      ...(logisticCompanyId ? { logisticCompanyId } : {}),
    };

    const response = await Axios.get("users/status/user", { params });
    return { success: true, data: response.data, error: null };
  } catch (err) {
    return {
      success: false,
      data: null,
      error: err.response?.data || { message: "No se pudo obtener repartidores activos" },
    };
  }
};

export const getActiveUsers = async (page = 1, limit = 10, searchTerm = "", role = "", companyName = "", companyBusinessIdentifier = "", companyId = "") => {
  let url = `users/status/user?status=active&page=${page}&limit=${limit}`;

  if (searchTerm !== "" && searchTerm !== null && searchTerm !== "null") {
    url += `&email=${encodeURIComponent(searchTerm)}`;
  }

  if (role !== "" && role !== null && role !== "all") {
    url += `&role=${encodeURIComponent(role)}`;
  }

  if (companyId !== "" && companyId !== null && companyId !== "all") {
    url += `&companyId=${encodeURIComponent(companyId)}`;
  }

  if (companyName !== "" && companyName !== null && companyName !== "all") {
    url += `&companyName=${encodeURIComponent(companyName)}`;
  }

  if (companyBusinessIdentifier !== "" && companyBusinessIdentifier !== null && companyBusinessIdentifier !== "all") {
    url += `&companyBusinessIdentifier=${encodeURIComponent(companyBusinessIdentifier)}`;
  }

  return await Axios.get(url).catch((err) => {
    return { error: err.response?.data };
  });
};

export const deleteUserApi = async ({ userId }) => {
  const deletedUser = await Axios.delete(`delete-user/${userId}`).catch(
    (err) => {
      return { error: err.response.data };
    }
  );

  return deletedUser;
};

// El id de la empresa logística llega a veces poblado como objeto (depende de si
// el guard repobló req.user desde la BD) y el DTO del backend lo declara
// `@IsString()`: mandarlo como objeto es un 400. Normalizamos siempre.
const toId = (value) => {
  const id = value?._id ?? value;
  return id ? String(id) : null;
};

/**
 * Acepta un repartidor de la cola "Por aceptar" (/admin/users?pending=true).
 *
 * La cola mezcla dos casos de entrada distintos que necesitan el MISMO estado final:
 *  - se registró eligiendo al operador → llega con active=false, newUser=true y
 *    logisticCompanyId ya seteado; basta con activarlo.
 *  - quedó sin operador (`withoutLogisticCompany`) → llega ya con active=true, así
 *    que activarlo es un no-op: lo que le falta es el logisticCompanyId.
 * Por eso se escriben los cuatro campos y no solo `active`. Sin esto, los del
 * segundo caso volverían a aparecer en la cola indefinidamente.
 *
 * `active` es el gate de login del BFF: mientras sea false el repartidor no puede
 * entrar a la app, por más que el resto de sus datos esté completo.
 */
export const acceptDeliveryUserApi = async ({ userId, logisticCompanyId }) => {
  const operatorId = toId(logisticCompanyId);
  if (!operatorId) {
    return { error: { message: "No tienes empresa logística asociada para asignar." } };
  }

  try {
    const { data } = await Axios.patch(`users/${userId}`, {
      active: true,
      newUser: false,
      trash: false,
      logisticCompanyId: operatorId,
    });
    return { data };
  } catch (err) {
    return {
      error: err.response?.data || { message: "No se pudo habilitar al repartidor" },
    };
  }
};

export const updateUserApi = async ({ userId, values }) => {
  try {
    const userUpdated = await Axios.patch(`users/${userId}`, {
      ...values,
    }).catch((err) => {
      return { error: err.response?.data || err.message };
    });

    if (userUpdated.error) {
      return userUpdated;
    }

    return { data: userUpdated.data };
  } catch (error) {
    return { error: error.message };
  }
};

// Datos bancarios del PROPIO usuario (sección "Datos bancarios" de Mi Cuenta,
// visible solo para vendedores). Va por el endpoint self-service del backend
// (la identidad sale del token) y NO por PATCH users/:id: el subdocumento
// `bankData` está gateado ahí y además el PUT exige el objeto completo.
export const updateMyBankData = async (values) => {
  try {
    const res = await Axios.put("users/me/bank-data", values);
    return { data: res.data };
  } catch (err) {
    return {
      error: err.response?.data || { message: "No se pudieron guardar los datos bancarios" },
    };
  }
};


// API V2 - 04-07
// nuevos metodos segun curlV2 - form data
// Crear un usuario de delivery
export const createDeliveryUser = async (data) => {
  const formData = new FormData();
  formData.append("name", data.name);
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("driveLicense", data.driveLicense);
  formData.append("carDocument", data.carDocument);
  formData.append("carId", data.carId);
  formData.append("namePayPerson", data.namePayPerson);
  formData.append("rutPayPerson", data.rutPayPerson);
  formData.append("accountType", data.accountType);
  formData.append("bank", data.bank);
  formData.append("role", "delivery");

  //console.log("FormData entries delivery:");
  formData.forEach((value, key) => {
    //console.log(key, value);
  });

  try {
    const response = await AxiosV2.post("/users", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "authorization": `Bearer ${data.token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response ? error.response.data.message : error.message
    );
  }
};

// Actualizar un usuario de delivery
export const updateDeliveryUser = async (id, data) => {
  const formData = new FormData();
  formData.append("name", data.name);
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("driveLicense", data.driveLicense);
  formData.append("carDocument", data.carDocument);
  formData.append("carId", data.carId);
  formData.append("namePayPerson", data.namePayPerson);
  formData.append("rutPayPerson", data.rutPayPerson);
  formData.append("accountType", data.accountType);
  formData.append("bank", data.bank);
  formData.append("role", "delivery");
  //console.log("contenido: ", formData);

  try {
    const response = await AxiosV2.put(`/users/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authorization: `Bearer ${data.token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response ? error.response.data.message : error.message
    );
  }
};

// Crear un usuario de ventas
export const createClientUser = async (data) => {
  //console.log("in createClientUser data:::", data);
  //console.log("in token data:::", data.token);
  const formData = new FormData();
  formData.append("legalName", data.legalName);
  formData.append("rut", data.rut);
  formData.append("fantasyName", data.fantasyName);
  formData.append("fiscalAddress", data.fiscalAddress);
  formData.append("name", data.name);
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("senderAddress", data.senderAddress);
  formData.append("preferedHour", data.preferedHour);
  formData.append("namePayPerson", data.namePayPerson);
  formData.append("emailPayPerson", data.emailPayPerson);
  formData.append("role", "client");

  //console.log("FormData entries:");
  formData.forEach((value, key) => {
    //console.log(key, value);
  });

  try {
    const response = await AxiosV2.post("/users", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "authorization": `Bearer ${data.token}`,
      },
    });
    return response.data;
  } catch (error) {
    // The caller shows error.message, so it must be readable text: an array of
    // class-validator messages is joined with ' · ', and transport noise
    // ('Network Error', 'Request failed with status code 400') never leaks in.
    const err = new Error(apiErrorMessage(error) ?? "");
    err.messages = apiBodyMessages(apiErrorData(error));
    err.status = apiErrorStatus(error);
    throw err;
  }
};

// Actualizar un usuario de ventas
export const updateClientUser = async (id, data) => {
  const formData = new FormData();
  formData.append("legalName", data.legalName);
  formData.append("rut", data.rut);
  formData.append("fantasyName", data.fantasyName);
  formData.append("fiscalAddress", data.fiscalAddress);
  formData.append("name", data.name);
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("senderAddress", data.senderAddress);
  formData.append("preferedHour", data.preferedHour);
  formData.append("namePayPerson", data.namePayPerson);
  formData.append("emailPayPerson", data.emailPayPerson);
  formData.append("role", "client");

  try {
    const response = await AxiosV2.post(`/users/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        authorization: `Bearer ${data.token}`,
      },
    });
    return response.data;
  } catch (error) {
    // Same rich error as createClientUser: both feed the same form toast.
    const err = new Error(apiErrorMessage(error) ?? "");
    err.messages = apiBodyMessages(apiErrorData(error));
    err.status = apiErrorStatus(error);
    throw err;
  }
};

// Crear un usuario para una empresa (usuario inicial del contrato)
export const createCompanyUser = async (data) => {
  try {
    const response = await Axios.post("users", data);
    return response.data;
  } catch (err) {
    console.error("Error in createCompanyUser:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

/**
 * Resuelve un array de ObjectIds de usuario a nombres legibles.
 * Devuelve un mapa { [id]: { displayName, role } }.
 * Para clientes devuelve el nombre de la empresa; para el resto, nombre + apellido + rol.
 *
 * @param {string[]} ids - Array de ObjectId strings (max 100)
 */
export const batchResolveUsers = async (ids = []) => {
  if (ids.length === 0) return {};
  try {
    const response = await Axios.post("users/batch-resolve", { ids });
    return response.data ?? {};
  } catch (err) {
    console.error("Error resolviendo usuarios:", err);
    return {};
  }
};

// Verificar si un usuario existe por su email o userIdentifier.
// Vive en microservice-users (dueño del modelo User) y no bajo `public/`: la ruta
// anterior `public/users/check` se eliminó del gateway al migrar el registro
// público a controladores por rol, y el 404 dejaba el wizard de contratos sin salida.
export const checkUserExists = async (email, userIdentifier = "") => {
  try {
    const response = await Axios.post("users/check", {
      email: email.toLowerCase().trim(),
      userIdentifier: userIdentifier || "",
    });
    return response.data;
  } catch (err) {
    console.error("Error checking user existence:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};